import { useSnackbar } from 'notistack'
import React, { FC, useEffect, useRef, useState } from 'react'
import { CabinetSizesSubType, CategoryPropsType, CategoryType, CornerCabinetSizesSubType } from '../../../api/appAPI'
import { EnqueueSnackbarType, showMessage } from '../../common/Helpers'
import CategoryUIBox from './CategoryUIBox'
import CornerCategoryUIBox from './CornerCategoryUIBox'

interface IProps {
    category: CategoryType

    editCategory(id: number, category: CategoryPropsType, enqueueSnackbar: EnqueueSnackbarType): void
}

const EditCategory: FC<IProps> = ({ category, editCategory, ...props }) => {
    const idRef = useRef(category.id)

    const [activeRole, setActiveRole] = useState('retail')

    const [categoryProps, setCategoryProps] = useState({
        name: category.name,
        displayName: category.displayName,
        forCorner: category.forCorner,
        retail: category.retail,
        dealer: category.dealer,
        designer: category.designer
    })

    const categoryPropsRef = useRef(categoryProps)

    const [needSave, setNeedSave] = useState(false)
    const needSaveRef = useRef(needSave)

    const { enqueueSnackbar } = useSnackbar()

    useEffect(() => {
        idRef.current = category.id

        setCategoryProps({
            name: category.name,
            displayName: category.displayName,
            forCorner: category.forCorner,
            retail: category.retail,
            dealer: category.dealer,
            designer: category.designer
        })

        setNeedSave(false)
    }, [category])

    useEffect(() => {
        categoryPropsRef.current = categoryProps
        needSaveRef.current = needSave
    }, [needSave, categoryProps])

    useEffect(() => () => {
        if (needSaveRef.current) {
            if (window?.confirm('Save category?')) {
                handleSaveClick()
            }
        }
    }, [category])

    const handleNameChange = (event: React.ChangeEvent<{ value: string }>) => {
        setCategoryProps({
            ...categoryProps,
            name: event.target.value.replace(/['"]/, '')
        })
        setNeedSave(true)
    }

    const handleDisplayNameChange = (event: React.ChangeEvent<{ value: string }>) => {
        setCategoryProps({
            ...categoryProps,
            displayName: event.target.value.replace(/['"]/, '')
        })
        setNeedSave(true)
    }

    const setRetail = (cabSizes: CabinetSizesSubType | CornerCabinetSizesSubType) => {
        setCategoryProps({
            ...categoryProps,
            retail: cabSizes
        })
        setNeedSave(true)
    }
    const setDealer = (cabSizes: CabinetSizesSubType | CornerCabinetSizesSubType) => {
        setCategoryProps({
            ...categoryProps,
            dealer: cabSizes
        })
        setNeedSave(true)
    }
    const setDesigner = (cabSizes: CabinetSizesSubType | CornerCabinetSizesSubType) => {
        setCategoryProps({
            ...categoryProps,
            designer: cabSizes
        })
        setNeedSave(true)
    }

    const handleSaveClick = () => {
        const name = categoryPropsRef.current.name
        const displayName = categoryPropsRef.current.displayName
        const retail = categoryPropsRef.current.retail as CabinetSizesSubType
        const dealer = categoryPropsRef.current.dealer as CabinetSizesSubType
        const designer = categoryPropsRef.current.designer as CabinetSizesSubType

        if (name === '') {
            showMessage(enqueueSnackbar, "Enter material name")
            return
        }

        if (retail.width.values.length === 0) {
            showMessage(enqueueSnackbar, 'Write at least one value of width of retail')
            return
        }
        if (!retail.width.values.includes(retail.width.default)) {
            showMessage(enqueueSnackbar, 'Choose default value for width of retail')
            return
        }
        if (retail.height.values.length === 0) {
            showMessage(enqueueSnackbar, 'Write at least one value of height of retail')
            return
        }
        if (!retail.height.values.includes(retail.height.default)) {
            showMessage(enqueueSnackbar, 'Choose default value for height of retail')
            return
        }
        if (retail.depth.values.length === 0) {
            showMessage(enqueueSnackbar, 'Write at least one value of depth of retail')
            return
        }
        if (!retail.depth.values.includes(retail.depth.default)) {
            showMessage(enqueueSnackbar, 'Choose default value for depth of retail')
            return
        }


        if (dealer.width.values.length === 0) {
            showMessage(enqueueSnackbar, 'Write at least one value of width of dealer')
            return
        }
        if (!dealer.width.values.includes(dealer.width.default)) {
            showMessage(enqueueSnackbar, 'Choose default value for width of dealer')
            return
        }
        if (dealer.height.values.length === 0) {
            showMessage(enqueueSnackbar, 'Write at least one value of height of dealer')
            return
        }
        if (!dealer.height.values.includes(dealer.height.default)) {
            showMessage(enqueueSnackbar, 'Choose default value for height of dealer')
            return
        }
        if (dealer.depth.values.length === 0) {
            showMessage(enqueueSnackbar, 'Write at least one value of depth of dealer')
            return
        }
        if (!dealer.depth.values.includes(dealer.depth.default)) {
            showMessage(enqueueSnackbar, 'Choose default value for depth of dealer')
            return
        }

        if (designer.width.values.length === 0) {
            showMessage(enqueueSnackbar, 'Write at least one value of width of designer')
            return
        }
        if (!designer.width.values.includes(designer.width.default)) {
            showMessage(enqueueSnackbar, 'Choose default value for width of designer')
            return
        }
        if (designer.height.values.length === 0) {
            showMessage(enqueueSnackbar, 'Write at least one value of height of designer')
            return
        }
        if (!designer.height.values.includes(designer.height.default)) {
            showMessage(enqueueSnackbar, 'Choose default value for height of designer')
            return
        }
        if (designer.depth.values.length === 0) {
            showMessage(enqueueSnackbar, 'Write at least one value of depth of designer')
            return
        }
        if (!designer.depth.values.includes(designer.depth.default)) {
            showMessage(enqueueSnackbar, 'Choose default value for depth of designer')
            return
        }

        needSaveRef.current = false
        setNeedSave(false)

        const categoryProps = {
            name,
            displayName,
            retail,
            dealer,
            designer
        } as CategoryPropsType

        editCategory(idRef.current, categoryProps, enqueueSnackbar)
    }

    const handleCornerSaveClick = () => {
        const name = categoryPropsRef.current.name
        const displayName = categoryPropsRef.current.displayName
        const forCorner = categoryPropsRef.current.forCorner
        const retail = categoryPropsRef.current.retail as CornerCabinetSizesSubType
        const dealer = categoryPropsRef.current.dealer as CornerCabinetSizesSubType
        const designer = categoryPropsRef.current.designer as CornerCabinetSizesSubType

        if (name === '') {
            showMessage(enqueueSnackbar, "Enter material name")
            return
        }

        if (retail.width1.values.length === 0) {
            showMessage(enqueueSnackbar, 'Write at least one value of width1 of designer')
            return
        }
        if (!retail.width1.values.includes(retail.width1.default)) {
            showMessage(enqueueSnackbar, 'Choose default value for width1 of designer')
            return
        }
        if (retail.width2.values.length === 0) {
            showMessage(enqueueSnackbar, 'Write at least one value of width2 of designer')
            return
        }
        if (!retail.width2.values.includes(retail.width2.default)) {
            showMessage(enqueueSnackbar, 'Choose default value for width2 of designer')
            return
        }

        if (retail.height.values.length === 0) {
            showMessage(enqueueSnackbar, 'Write at least one value of height of designer')
            return
        }
        if (!retail.height.values.includes(retail.height.default)) {
            showMessage(enqueueSnackbar, 'Choose default value for height of designer')
            return
        }

        if (retail.depth1.values.length === 0) {
            showMessage(enqueueSnackbar, 'Write at least one value of depth1 of designer')
            return
        }
        if (!retail.depth1.values.includes(retail.depth1.default)) {
            showMessage(enqueueSnackbar, 'Choose default value for depth1 of designer')
            return
        }
        if (retail.depth2.values.length === 0) {
            showMessage(enqueueSnackbar, 'Write at least one value of depth2 of designer')
            return
        }
        if (!retail.depth2.values.includes(retail.depth2.default)) {
            showMessage(enqueueSnackbar, 'Choose default value for depth2 of designer')
            return
        }

        if (retail.backWidth1.values.length === 0) {
            showMessage(enqueueSnackbar, 'Write at least one value of backWidth1 of designer')
            return
        }
        if (!retail.backWidth1.values.includes(retail.backWidth1.default)) {
            showMessage(enqueueSnackbar, 'Choose default value for backWidth1 of designer')
            return
        }
        if (retail.backWidth2.values.length === 0) {
            showMessage(enqueueSnackbar, 'Write at least one value of backWidth2 of designer')
            return
        }
        if (!retail.backWidth2.values.includes(retail.backWidth2.default)) {
            showMessage(enqueueSnackbar, 'Choose default value for backWidth2 of designer')
            return
        }


        if (dealer.width1.values.length === 0) {
            showMessage(enqueueSnackbar, 'Write at least one value of width1 of designer')
            return
        }
        if (!dealer.width1.values.includes(dealer.width1.default)) {
            showMessage(enqueueSnackbar, 'Choose default value for width1 of designer')
            return
        }
        if (dealer.width2.values.length === 0) {
            showMessage(enqueueSnackbar, 'Write at least one value of width2 of designer')
            return
        }
        if (!dealer.width2.values.includes(dealer.width2.default)) {
            showMessage(enqueueSnackbar, 'Choose default value for width2 of designer')
            return
        }

        if (dealer.height.values.length === 0) {
            showMessage(enqueueSnackbar, 'Write at least one value of height of designer')
            return
        }
        if (!dealer.height.values.includes(dealer.height.default)) {
            showMessage(enqueueSnackbar, 'Choose default value for height of designer')
            return
        }

        if (dealer.depth1.values.length === 0) {
            showMessage(enqueueSnackbar, 'Write at least one value of depth1 of designer')
            return
        }
        if (!dealer.depth1.values.includes(dealer.depth1.default)) {
            showMessage(enqueueSnackbar, 'Choose default value for depth1 of designer')
            return
        }
        if (dealer.depth2.values.length === 0) {
            showMessage(enqueueSnackbar, 'Write at least one value of depth2 of designer')
            return
        }
        if (!dealer.depth2.values.includes(dealer.depth2.default)) {
            showMessage(enqueueSnackbar, 'Choose default value for depth2 of designer')
            return
        }

        if (dealer.backWidth1.values.length === 0) {
            showMessage(enqueueSnackbar, 'Write at least one value of backWidth1 of designer')
            return
        }
        if (!dealer.backWidth1.values.includes(dealer.backWidth1.default)) {
            showMessage(enqueueSnackbar, 'Choose default value for backWidth1 of designer')
            return
        }
        if (dealer.backWidth2.values.length === 0) {
            showMessage(enqueueSnackbar, 'Write at least one value of backWidth2 of designer')
            return
        }
        if (!dealer.backWidth2.values.includes(dealer.backWidth2.default)) {
            showMessage(enqueueSnackbar, 'Choose default value for backWidth2 of designer')
            return
        }

        if (designer.width1.values.length === 0) {
            showMessage(enqueueSnackbar, 'Write at least one value of width1 of designer')
            return
        }
        if (!designer.width1.values.includes(designer.width1.default)) {
            showMessage(enqueueSnackbar, 'Choose default value for width1 of designer')
            return
        }
        if (designer.width2.values.length === 0) {
            showMessage(enqueueSnackbar, 'Write at least one value of width2 of designer')
            return
        }
        if (!designer.width2.values.includes(designer.width2.default)) {
            showMessage(enqueueSnackbar, 'Choose default value for width2 of designer')
            return
        }

        if (designer.height.values.length === 0) {
            showMessage(enqueueSnackbar, 'Write at least one value of height of designer')
            return
        }
        if (!designer.height.values.includes(designer.height.default)) {
            showMessage(enqueueSnackbar, 'Choose default value for height of designer')
            return
        }

        if (designer.depth1.values.length === 0) {
            showMessage(enqueueSnackbar, 'Write at least one value of depth1 of designer')
            return
        }
        if (!designer.depth1.values.includes(designer.depth1.default)) {
            showMessage(enqueueSnackbar, 'Choose default value for depth1 of designer')
            return
        }
        if (designer.depth2.values.length === 0) {
            showMessage(enqueueSnackbar, 'Write at least one value of depth2 of designer')
            return
        }
        if (!designer.depth2.values.includes(designer.depth2.default)) {
            showMessage(enqueueSnackbar, 'Choose default value for depth2 of designer')
            return
        }

        if (designer.backWidth1.values.length === 0) {
            showMessage(enqueueSnackbar, 'Write at least one value of backWidth1 of designer')
            return
        }
        if (!designer.backWidth1.values.includes(designer.backWidth1.default)) {
            showMessage(enqueueSnackbar, 'Choose default value for backWidth1 of designer')
            return
        }
        if (designer.backWidth2.values.length === 0) {
            showMessage(enqueueSnackbar, 'Write at least one value of backWidth2 of designer')
            return
        }
        if (!designer.backWidth2.values.includes(designer.backWidth2.default)) {
            showMessage(enqueueSnackbar, 'Choose default value for backWidth2 of designer')
            return
        }

        needSaveRef.current = false
        setNeedSave(false)

        const categoryProps = {
            name,
            displayName,
            forCorner,
            retail,
            dealer,
            designer
        } as CategoryPropsType

        editCategory(idRef.current, categoryProps, enqueueSnackbar)
    }

    return (!categoryProps.forCorner
        ? <CategoryUIBox
            title='Edit Category'
            name={{
                value: categoryProps.name,
                onChange: handleNameChange
            }}
            displayName={{
                value: categoryProps.displayName,
                onChange: handleDisplayNameChange
            }}

            type={{
                value: categoryProps.forCorner ? 'corner' : 'normal',
                onChange: (e: any) => { }
            }}

            activeRole={{
                value: activeRole,
                onChange: (e: React.ChangeEvent<{ value: string }>) => {
                    setActiveRole(e.target.value)
                }
            }}

            activeRoleSizes={{
                value: (activeRole === 'retail' ? categoryProps.retail : activeRole === 'dealer' ? categoryProps.dealer : categoryProps.designer) as CabinetSizesSubType,
                onChange: activeRole === 'retail' ? setRetail : activeRole === 'dealer' ? setDealer : setDesigner
            }}

            needSave={needSave}
            onButtonClick={handleSaveClick}
        />
        : <CornerCategoryUIBox
            title='Edit Category'
            name={{
                value: categoryProps.name,
                onChange: handleNameChange
            }}
            displayName={{
                value: categoryProps.displayName,
                onChange: handleDisplayNameChange
            }}
            type={{
                value: categoryProps.forCorner ? 'corner' : 'normal',
                onChange: (e: any) => { }
            }}

            activeRole={{
                value: activeRole,
                onChange: (e: React.ChangeEvent<{ value: string }>) => {
                    setActiveRole(e.target.value)
                }
            }}

            activeRoleSizes={{
                value: (activeRole === 'retail' ? categoryProps.retail : activeRole === 'dealer' ? categoryProps.dealer : categoryProps.designer) as CornerCabinetSizesSubType,
                onChange: activeRole === 'retail' ? setRetail : activeRole === 'dealer' ? setDealer : setDesigner
            }}

            needSave={needSave}
            onButtonClick={handleCornerSaveClick}
        />
    )
}

export default EditCategory